import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Waves, HeartHandshake, MapPin, Star, Quote, Instagram } from "lucide-react";
import { MaskLine, Reveal } from "@/components/Reveal";
import { TaglineMarquee } from "@/components/TaglineMarquee";
import { PhotoMarquee } from "@/components/PhotoMarquee";
import { HomeBookTeaser } from "@/components/books/HomeBookTeaser";
import { SERVICES, MANIFESTO, IMAGES, TESTIMONIALS, VIDEOS } from "@/lib/content";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end pb-16 pt-32 overflow-hidden">
        <div className="noise z-10" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-10 items-center relative z-20">
          <div className="lg:col-span-7">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="overline mb-8 flex items-center gap-3" style={{ color: "var(--gold)" }}>
              <span className="inline-block w-10 h-px" style={{ background: "var(--gold)" }} /> Private Mobile Swim Instruction — Boca Raton &amp; Palm Beach, FL
            </motion.p>
            <h1 className="font-display leading-[0.98] tracking-tight text-[3.4rem] sm:text-7xl lg:text-[6.2rem]" style={{ color: "var(--navy)" }}>
              <MaskLine delay={0.1}>Where little</MaskLine>
              <MaskLine delay={0.24} className="italic" >swimmers find</MaskLine>
              <MaskLine delay={0.38}>their confidence.</MaskLine>
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="font-body font-light text-lg md:text-xl mt-8 max-w-xl leading-relaxed" style={{ color: "var(--muted)" }}>
              Personalized one-on-one lessons for babies, toddlers and children — taught with love,
              patience and structure in the comfort of your own home pool.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="flex flex-wrap gap-4 mt-10">
              <Link to="/contact" className="btn-primary" data-testid="hero-cta">Request Availability <ArrowUpRight size={18} /></Link>
              <Link to="/intensive" className="btn-secondary" data-testid="hero-intensive">The Breakthrough Intensive</Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1 }} className="relative rounded-[2rem] overflow-hidden border" style={{ borderColor: "rgba(212,175,55,0.4)" }}>
              <motion.img style={{ y: imgY, scale: imgScale }} src={IMAGES.heroBabySunset} alt="Milly holding a baby swimmer in a Boca Raton pool at golden hour" className="w-full h-[420px] lg:h-[560px] object-cover object-[40%_center]" data-testid="hero-image" />
              <div className="bg-white p-5 border-t" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
                <p className="font-display text-2xl" style={{ color: "var(--navy)" }}>American Red Cross Certified</p>
                <p className="font-body font-light text-sm" style={{ color: "var(--muted)" }}>Loving, patient, structured &amp; results-driven instruction.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <TaglineMarquee />

      {/* INTRO / DIFFERENCE */}
      <section className="py-28" data-testid="home-intro">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline mb-6" style={{ color: "var(--gold)" }}>Not a swim school</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--navy)" }}>
                A personalized private-swim experience built around <span className="italic">your child.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <p className="font-body font-light text-lg md:text-xl leading-relaxed" style={{ color: "var(--muted)" }}>
                Mimi &amp; Me Swimming isn't a large, one-size-fits-all program. Every swimmer is
                different, so lessons are customized to your child's age, personality, comfort level
                and goals. We work on far more than swimming — trust, breath control, floating, safe
                pool exits and lifelong water-safety skills.
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-3 gap-6 mt-12">
              {[{ i: HeartHandshake, t: "Trust First", d: "We build a real relationship before progress." }, { i: ShieldCheck, t: "Water Safety", d: "Independent, life-saving skills — no false confidence." }, { i: Waves, t: "Your Home Pool", d: "We come to you across Boca Raton & Palm Beach." }].map((c, idx) => (
                <Reveal key={c.t} delay={0.2 + idx * 0.1}>
                  <div className="p-6 rounded-2xl bg-white border h-full" style={{ borderColor: "rgba(212,175,55,0.2)" }}>
                    <c.i size={26} style={{ color: "var(--gold)" }} />
                    <h3 className="font-display text-2xl mt-4" style={{ color: "var(--navy)" }}>{c.t}</h3>
                    <p className="font-body font-light text-sm mt-2" style={{ color: "var(--muted)" }}>{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PHOTO SHOWCASE */}
      <PhotoMarquee />

      {/* MOMENTS — VIDEO STRIP */}
      <section className="py-24" data-testid="home-moments">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
              <div>
                <p className="overline mb-4" style={{ color: "var(--gold)" }}>Moments in the water</p>
                <h2 className="font-display text-4xl md:text-5xl max-w-2xl" style={{ color: "var(--navy)" }}>
                  Real lessons, <span className="italic">real joy.</span>
                </h2>
              </div>
              <Link to="/gallery" className="btn-secondary !py-3 !px-6" data-testid="home-moments-link">Watch more <ArrowUpRight size={16} /></Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {VIDEOS.slice(0, 3).map((v, i) => (
              <Reveal key={v.src} delay={i * 0.1}>
                <div className="rounded-2xl overflow-hidden border bg-white h-full flex flex-col" style={{ borderColor: "rgba(212,175,55,0.3)" }} data-testid={`home-moment-${i}`}>
                  <video className="w-full aspect-video object-cover bg-black" src={v.src} poster={v.poster} controls playsInline preload="none" />
                  <div className="p-5">
                    <h3 className="font-display text-xl" style={{ color: "var(--navy)" }}>{v.title}</h3>
                    <p className="font-body font-light text-sm mt-1 leading-relaxed" style={{ color: "var(--muted)" }}>{v.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-24" style={{ backgroundColor: "var(--sand)" }} data-testid="home-services">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>What we offer</p>
            <h2 className="font-display text-4xl md:text-5xl mb-14" style={{ color: "var(--navy)" }}>Ways to swim with Mimi</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.key} delay={i * 0.08}>
                <Link to={s.to} className="group block relative rounded-3xl overflow-hidden h-[340px]" data-testid={`service-${s.key}`}>
                  <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,27,61,0.1) 0%, rgba(11,27,61,0.85) 100%)" }} />
                  <div className="absolute bottom-0 p-8">
                    <h3 className="font-display text-3xl" style={{ color: "var(--cream)" }}>{s.title}</h3>
                    <p className="font-body font-light text-sm mt-2 max-w-md" style={{ color: "var(--powder)" }}>{s.blurb}</p>
                    <span className="inline-flex items-center gap-2 mt-4 overline" style={{ color: "var(--gold)" }}>Learn more <ArrowUpRight size={16} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-28" data-testid="home-manifesto">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>Teaching philosophy</p>
            <h2 className="font-display text-4xl md:text-5xl mb-16 max-w-2xl" style={{ color: "var(--navy)" }}>
              The strongest results come from four simple things.
            </h2>
          </Reveal>
          <div className="space-y-4">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.06}>
                <div className="grid md:grid-cols-12 gap-6 items-center py-8 border-t" style={{ borderColor: "rgba(212,175,55,0.25)" }}>
                  <span className="md:col-span-2 font-display text-6xl" style={{ color: "rgba(212,175,55,0.5)" }}>{m.n}</span>
                  <h3 className="md:col-span-3 font-display text-3xl" style={{ color: "var(--navy)" }}>{m.title}</h3>
                  <p className="md:col-span-7 font-body font-light text-lg leading-relaxed" style={{ color: "var(--muted)" }}>{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LUXURY / MOBILE */}
      <section className="py-24" data-testid="home-luxury">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
              <img src={IMAGES.millyCapSelfie} alt="Milly with a happy student during a private lesson" className="w-full aspect-[4/5] object-cover object-center" />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>The white-glove experience</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--navy)" }}>
              A premium service that <span className="italic">comes to you.</span>
            </h2>
            <p className="font-body font-light text-lg mt-6 leading-relaxed" style={{ color: "var(--muted)" }}>
              No crowded swim schools or waiting rooms. Milly arrives at your door and turns your own
              pool into a calm, private classroom — a truly personal, upscale experience from the very
              first lesson.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SUNSET FULL-BLEED */}
      <SunsetBand />

      {/* TESTIMONIALS */}
      <section className="py-24" data-testid="home-testimonials">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>What parents say</p>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
              <h2 className="font-display text-4xl md:text-5xl max-w-2xl" style={{ color: "var(--navy)" }}>
                Loved by Boca Raton <span className="italic">families.</span>
              </h2>
              <Link to="/testimonials" className="btn-secondary !py-3 !px-6" data-testid="home-reviews-link">Read all reviews <ArrowUpRight size={16} /></Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 3).map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="p-8 rounded-2xl bg-white border h-full flex flex-col" style={{ borderColor: "rgba(212,175,55,0.25)" }}>
                  <Quote size={28} style={{ color: "var(--gold)" }} />
                  <div className="flex gap-1 my-4">
                    {[...Array(5)].map((_, s) => <Star key={s} size={15} fill="var(--gold)" style={{ color: "var(--gold)" }} />)}
                  </div>
                  <p className="font-accent italic text-lg leading-relaxed flex-1" style={{ color: "var(--navy)" }}>"{t.quote}"</p>
                  <p className="overline mt-6 pt-6 border-t" style={{ color: "var(--gold)", borderColor: "rgba(212,175,55,0.2)" }}>{t.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOLLOW ALONG */}
      <section className="pb-8" data-testid="home-instagram">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <a
            href="https://instagram.com/mila_pilar444" target="_blank" rel="noreferrer"
            className="group flex flex-col sm:flex-row items-center justify-between gap-6 rounded-3xl p-8 md:p-10 transition-transform hover:-translate-y-1"
            style={{ backgroundColor: "var(--navy)" }}
            data-testid="home-instagram-cta"
          >
            <div className="flex items-center gap-5">
              <span className="flex items-center justify-center w-14 h-14 rounded-full flex-shrink-0" style={{ backgroundColor: "rgba(212,175,55,0.15)" }}>
                <Instagram size={26} style={{ color: "var(--gold)" }} />
              </span>
              <div>
                <p className="overline mb-1" style={{ color: "var(--gold)" }}>Follow along</p>
                <h3 className="font-display text-2xl md:text-3xl" style={{ color: "var(--cream)" }}>See the smiles on Instagram</h3>
              </div>
            </div>
            <span className="flex items-center gap-2 font-body text-lg group-hover:gap-3 transition-all" style={{ color: "var(--cream)" }}>
              @mila_pilar444 <ArrowUpRight size={20} style={{ color: "var(--gold)" }} />
            </span>
          </a>
        </div>
      </section>

      <HomeBookTeaser />

      {/* MERCH TEASER */}
      <section className="py-24" style={{ backgroundColor: "var(--sand)" }} data-testid="home-merch-teaser">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="grid grid-cols-2 gap-4">
              <div className="overflow-hidden rounded-3xl border" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
                <img src={IMAGES.merchFluorescent} alt="Mimi & Me fluorescent safety rash guards" className="w-full h-[400px] object-cover object-top" />
              </div>
              <div className="overflow-hidden rounded-3xl border bg-white flex items-center justify-center p-4" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
                <img src={IMAGES.merchKickboard} alt="Junior Swimming Kickboard" className="w-full h-auto max-h-[368px] object-contain" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>Now reserving</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--navy)" }}>
              Gear that keeps them <span className="italic">safe &amp; seen.</span>
            </h2>
            <p className="font-body font-light text-lg mt-6 leading-relaxed" style={{ color: "var(--muted)" }}>
              High-visibility fluorescent rash guards — a water-safety essential — plus soft neutral
              styles and our coach-favorite Junior Kickboard for kick &amp; core. Reserve yours now and
              be first when they drop.
            </p>
            <Link to="/merch" className="btn-primary mt-8" data-testid="home-merch-cta">Shop the Collection <ArrowUpRight size={18} /></Link>
          </Reveal>
        </div>
      </section>

      {/* SIGNATURE QUOTE BANNER */}
      <section className="py-24" style={{ backgroundColor: "var(--sand)" }} data-testid="home-quote">
        <div className="max-w-2xl mx-auto px-6">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border shadow-[0_20px_60px_rgba(11,27,61,0.15)]" style={{ borderColor: "rgba(212,175,55,0.4)" }}>
              <img src={IMAGES.quoteBanner} alt="Every tiny moment, a lifetime of possibilities — Mimi & Me Swimming" className="w-full h-auto block" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PARALLAX CTA */}
      <ParallaxCTA />
    </div>
  );
}

const ParallaxCTA = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative h-[70vh] flex items-center justify-center overflow-hidden" data-testid="home-cta">
      <motion.img style={{ y, scale: 1.2 }} src={IMAGES.mansion} alt="Luxury South Florida estate with a private swimming pool" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(11,27,61,0.72)" }} />
      <div className="relative text-center px-6 max-w-3xl">
        <Reveal>
          <p className="overline mb-6" style={{ color: "var(--gold)" }}><MapPin size={14} className="inline mr-1" /> Mainly Boca Raton — serving Palm Beach County too</p>
          <h2 className="font-display text-4xl md:text-6xl leading-tight" style={{ color: "var(--cream)" }}>
            Start your child's <span className="italic">swim journey</span> today.
          </h2>
          <p className="font-body font-light text-lg mt-6" style={{ color: "var(--powder)" }}>
            Availability is limited and reserved by payment. Let's find the perfect time for your family.
          </p>
          <Link to="/contact" className="btn-primary mt-10 !bg-[var(--gold)] !text-[var(--navy)]" data-testid="cta-request">Request Availability <ArrowUpRight size={18} /></Link>
        </Reveal>
      </div>
    </section>
  );
};


const SunsetBand = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative h-[60vh] flex items-end overflow-hidden" data-testid="home-sunset">
      <motion.img style={{ y, scale: 1.2 }} src={IMAGES.sunsetLesson} alt="A private swim lesson in a Boca Raton backyard at sunset" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,27,61,0.15) 0%, rgba(11,27,61,0.75) 100%)" }} />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pb-14 w-full">
        <Reveal>
          <p className="overline mb-3" style={{ color: "var(--gold)" }}>Your own backyard</p>
          <h2 className="font-display text-4xl md:text-6xl leading-tight max-w-2xl" style={{ color: "var(--cream)" }}>
            From sunrise to sunset, <span className="italic">lessons come to you.</span>
          </h2>
        </Reveal>
      </div>
    </section>
  );
};
